// Creating a channel (ios/Sources/Views/CreateChannelSheet.swift, shared/API.md §19.3): one name
// field — the URL slug is derived, never asked for and never shown (CreateChannelForm) — a
// visibility choice, and an optional purpose. 400/403 come back as sentences a person can act on.
import { useState } from 'react';
import { data } from '../../../bridge/client';
import { CreateChannelForm, type Failure, type Visibility } from '../../../core/createChannelForm';
import { t } from '../../../i18n/i18n';
import { Button, Dialog, Segmented } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { useStableClose } from './dialogFocus';
import './people.css';

function failureText(failure: Failure): string {
  switch (failure) {
    case 'nameTaken':
      return t('createChannel.failedTaken');
    case 'notPermitted':
      return t('createChannel.failedPermission');
    default:
      return t('createChannel.failedOther');
  }
}

export function CreateChannelDialog({ onClose: onCloseProp }: { onClose(): void }) {
  const onClose = useStableClose(onCloseProp);
  const shell = useShell();
  const [displayName, setDisplayName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const submittable = CreateChannelForm.isSubmittable(displayName) && !busy;

  const create = async () => {
    if (!submittable) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await data.call('createChannel', { teamId: shell.team.id, displayName, purpose, visibility });
      if ('channelId' in result) {
        onClose();
        shell.openChannel(result.channelId);
        return;
      }
      setFailure(result.error);
    } catch {
      setFailure('other');
    }
    setBusy(false);
  };

  const footer = (
    <>
      <span className={`people-footer-hint${failure ? ' error' : ''}`} role={failure ? 'alert' : undefined}>
        {failure ? failureText(failure) : ''}
      </span>
      <Button kind="primary" onClick={() => void create()} disabled={!submittable}>
        {t('createChannel.create')}
      </Button>
    </>
  );

  return (
    <Dialog title={t('createChannel.title')} onClose={onClose} footer={footer} width={480}>
      {/* Not a <form>: the kit's Segmented buttons have no type and would SUBMIT it. Enter in the
          name field creates instead. */}
      <div className="people-form">
        <label className="people-field">
          <span className="people-field-label">{t('createChannel.name')}</span>
          <input
            className="people-input"
            value={displayName}
            maxLength={CreateChannelForm.maxDisplayName}
            autoFocus
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (failure === 'nameTaken') setFailure(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void create();
              }
            }}
            aria-invalid={failure === 'nameTaken'}
          />
          <span className="people-field-hint">{t('web.people.create.nameHint')}</span>
        </label>
        <label className="people-field">
          <span className="people-field-label">{t('createChannel.purpose')}</span>
          <textarea className="people-input" rows={3} value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={250} />
        </label>
        <div className="people-field">
          <Segmented<Visibility>
            value={visibility}
            onChange={(v) => {
              setVisibility(v);
              if (failure === 'notPermitted') setFailure(null);
            }}
            options={[
              { value: 'public', label: t('web.people.create.public') },
              { value: 'private', label: t('web.people.create.private') },
            ]}
          />
          <span className="people-field-hint">{visibility === 'private' ? t('createChannel.privateHint') : t('createChannel.publicHint')}</span>
        </div>
      </div>
    </Dialog>
  );
}
